import { UserProfileType } from '../peekapak-types/DataProtocolTypes';
import { getLocalStorage, setLocalStorage } from './aaa';
import { isElementaryStudent } from './UserManagementHelpers';

/**
 * Get the destination URL for a student when they skip the survey
 * @param userProfile - User profile to determine student type
 * @returns Destination path for senior students, or null for elementary (handled separately)
 */
export function getStudentDestination(userProfile: UserProfileType): string | null {
  if (isElementaryStudent(userProfile)) {
    // Elementary students go to /play via window.location.href, not route
    return null;
  }

  // Senior students go to studentportal
  // Check if they've completed check-in to determine specific destination
  const hasCheckedIn = getLocalStorage('isCheckInCompleteOrOptedOut');
  return hasCheckedIn === 'true' ? '/studentportal/toDos' : '/studentportal/checkin';
}

/**
 * Check if user has already made a choice for this campaign
 * @param campaignId - Campaign ID to check
 * @returns true if user has made a choice, false otherwise
 */
export function hasUserMadeChoice(campaignId: string): boolean {
  if (!campaignId) return false;

  try {
    const choices = JSON.parse(getLocalStorage('surveyChoices') || '{}');
    const choice = choices[campaignId];

    if (choice && choice.expiresAt > Date.now()) {
      return true;
    }

    // Clean up expired choice
    if (choice && choice.expiresAt <= Date.now()) {
      delete choices[campaignId];
      setLocalStorage('surveyChoices', JSON.stringify(choices));
    }
  } catch (error) {
    console.error('Error checking user survey choice:', error);
  }

  return false;
}

/**
 * Record user's choice for this campaign
 * @param campaignId - Campaign ID
 * @param choice - User's choice: 'take' or 'skip'
 */
export function recordUserChoice(campaignId: string, choice: 'take' | 'skip'): void {
  if (!campaignId) return;

  try {
    const choices = JSON.parse(getLocalStorage('surveyChoices') || '{}');

    // Store choice with 24 hour expiry
    choices[campaignId] = {
      choice,
      timestamp: Date.now(),
      expiresAt: Date.now() + 2 * 60 * 60 * 1000, // 2 hours
    };

    setLocalStorage('surveyChoices', JSON.stringify(choices));
  } catch (error) {
    console.error('Error recording user survey choice:', error);
  }
}

/**
 * Get user's previous choice for this campaign
 * @param campaignId - Campaign ID to check
 * @returns Previous choice or null if no valid choice exists
 */
export function getUserChoice(campaignId: string): 'take' | 'skip' | null {
  if (!campaignId) return null;

  try {
    const choices = JSON.parse(getLocalStorage('surveyChoices') || '{}');
    const choice = choices[campaignId];

    if (choice && choice.expiresAt > Date.now()) {
      return choice.choice;
    }
  } catch (error) {
    console.error('Error getting user survey choice:', error);
  }

  return null;
}

/**
 * Get the play URL for elementary students
 * @returns Play URL based on environment
 */
export function getPlayUrl(): string {
  if (window.location.hostname === 'localhost') {
    return 'https://test.peekapak.com/play/index.htm';
  } else {
    return `${window.location.protocol}//${window.location.hostname}/play/index.htm`;
  }
}
